import React, { useState } from 'react';
import ERROR_COVER_LQIP from './errorCoverLqip';

const SUPPORT_EMAIL = 'atendimento@consultamais.app';
const COVER_WEBP = '/img/erro/tecnico-carroca-800.webp';
const COVER_JPG = '/img/erro/tecnico-carroca-800.jpg';

/**
 * Textos por causa. Nenhuma variante mostra a mensagem técnica do erro:
 * o paciente vê o que aconteceu em linguagem simples e o que fazer agora.
 */
const VARIANTS = {
  update: {
    title: 'Há uma versão nova do app',
    text: 'Atualize para continuar de onde parou.',
    reassurance: 'Suas respostas e sua consulta continuam salvas.',
    primary: 'Atualizar',
  },
  offline: {
    title: 'Sem conexão com a internet',
    text: 'Confira o Wi-Fi ou os dados móveis e tente de novo.',
    reassurance: 'Nada foi perdido. Quando a conexão voltar, é só tentar de novo.',
    primary: 'Tentar de novo',
  },
  timeout: {
    title: 'A página está demorando para abrir',
    text: 'Pode ser a conexão. Tente de novo em alguns segundos.',
    reassurance: 'Suas respostas e sua consulta continuam salvas.',
    primary: 'Tentar de novo',
  },
  chat: {
    title: 'Não conseguimos abrir o chat',
    text: 'Toque em Tentar de novo para reabrir a conversa.',
    reassurance: 'Sua consulta não foi perdida. As mensagens e os documentos continuam salvos.',
    primary: 'Tentar de novo',
  },
  crash: {
    title: 'Algo não saiu como esperado',
    text: 'Tivemos um problema ao abrir esta tela. Tente de novo.',
    reassurance: 'Sua consulta e seus dados continuam salvos.',
    primary: 'Tentar de novo',
  },
};

const STALE_CHUNK_RE = /Loading(?: CSS)? chunk \S+ failed|Failed to fetch dynamically imported module|ChunkLoadError|Unexpected token ['"]?</i;

/** Escolhe a variante a partir do erro capturado (sem expor o texto dele). */
export function pickErrorVariant(error, fallback = 'crash') {
  try {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  } catch {}
  const message = String((error && error.message) || error || '');
  if (STALE_CHUNK_RE.test(message) || (error && error.name === 'ChunkLoadError')) return 'update';
  return fallback;
}

/**
 * Tela padrão de erro operacional. Fica no pacote principal (não é lazy):
 * a causa mais comum é justamente falha ao baixar um pedaço do app.
 * Capa: a mesma pintura do e-mail de erro técnico (mecânico consertando a
 * carroça). Sem rede, fica a miniatura borrada embutida.
 */
export default function ErrorScreen({
  variant = 'crash',
  onPrimary,
  primaryLabel,
  busy = false,
  onHome,
  onHelp,
  errorId,
  children,
}) {
  const v = VARIANTS[variant] || VARIANTS.crash;
  const [cover, setCover] = useState('loading');
  return (
    <div className="cm-error-screen" role="alert" aria-live="assertive" data-variant={variant}>
      <style>{`
        /* Fixa acima do rodapé de suporte (45) e abaixo do dialog da Sofia (9999). */
        .cm-error-screen {
          position: fixed;
          inset: 0;
          z-index: 9990;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          box-sizing: border-box;
          display: flex;
          padding: max(1.25rem, env(safe-area-inset-top, 0px)) 1rem max(1.25rem, env(safe-area-inset-bottom, 0px));
          background:
            radial-gradient(120% 60% at 50% 0%, rgba(232, 150, 76, 0.14), transparent 70%),
            #FBF7F0;
          color: #1E1E1E;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          -webkit-font-smoothing: antialiased;
        }
        .cm-error-screen *, .cm-error-screen *::before, .cm-error-screen *::after { box-sizing: border-box; }
        .support-fab, .support-fab-hint { display: none !important; }
        .cm-error-card {
          margin: auto;
          width: 100%;
          max-width: 27rem;
          background: #FFFFFF;
          border: 1px solid rgba(181, 138, 79, 0.22);
          border-radius: 1.5rem;
          box-shadow: 0 1px 2px rgba(60, 40, 20, 0.04), 0 18px 44px rgba(60, 40, 20, 0.10);
          overflow: hidden;
          text-align: center;
          animation: cm-error-in .35s cubic-bezier(.2, .7, .2, 1) both;
        }
        @keyframes cm-error-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }

        .cm-error-cover {
          position: relative;
          margin: 0;
          aspect-ratio: 16 / 9;
          max-height: 32vh;
          min-height: 128px;
          width: 100%;
          overflow: hidden;
          background: #C9773A;
        }
        .cm-error-cover-lqip {
          position: absolute;
          inset: -12px;
          background-size: cover;
          background-position: center 62%;
          filter: blur(8px) saturate(1.3) brightness(1.12);
        }
        .cm-error-cover img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center 62%;
          opacity: 0;
          transition: opacity .4s ease;
        }
        .cm-error-cover[data-state="ok"] img { opacity: 1; }
        .cm-error-cover[data-state="fail"] img { display: none; }
        .cm-error-cover::after {
          content: '';
          position: absolute;
          left: 0; right: 0; bottom: -1px;
          height: 24%;
          background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.85) 70%, #FFFFFF);
          pointer-events: none;
        }
        .cm-error-brand {
          position: absolute;
          top: .75rem;
          left: .75rem;
          z-index: 1;
          display: inline-flex;
          align-items: baseline;
          gap: .25rem;
          padding: .3rem .65rem;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.92);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
          font-size: .78rem;
          font-weight: 600;
          line-height: 1;
          letter-spacing: -0.01em;
        }
        .cm-error-brand-a { color: #0F1B3D; }
        .cm-error-brand-b { color: #D32F2F; }

        .cm-error-body { padding: .35rem 1.5rem 1.4rem; }
        .cm-error-title {
          margin: 0;
          font-size: 1.3rem;
          font-weight: 600;
          line-height: 1.28;
          letter-spacing: -0.005em;
          color: #1E1E1E;
          text-wrap: balance;
        }
        .cm-error-divider {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: .45rem;
          margin: .7rem 0 .75rem;
          color: #B58A4F;
          font-size: .5rem;
          user-select: none;
        }
        .cm-error-divider::before,
        .cm-error-divider::after {
          content: '';
          width: 2rem;
          height: 1px;
          background: linear-gradient(to right, rgba(181, 138, 79, 0), rgba(181, 138, 79, 0.6));
        }
        .cm-error-divider::after { transform: scaleX(-1); }
        .cm-error-text {
          margin: 0 0 1rem;
          color: #5F5F5F;
          font-size: .95rem;
          line-height: 1.5;
          text-wrap: pretty;
        }
        .cm-error-reassure {
          display: flex;
          align-items: flex-start;
          gap: .55rem;
          margin: 0 0 1.2rem;
          padding: .7rem .85rem;
          border-radius: .85rem;
          background: #FBF5EA;
          border: 1px solid rgba(181, 138, 79, 0.28);
          color: #5C4A2E;
          font-size: .85rem;
          line-height: 1.45;
          text-align: left;
        }
        .cm-error-reassure svg { flex: 0 0 auto; margin-top: .1rem; color: #B58A4F; }
        .cm-error-actions {
          display: flex;
          flex-direction: column;
          gap: .6rem;
        }
        .cm-error-btn {
          width: 100%;
          min-height: 48px;
          border-radius: .85rem;
          font-family: inherit;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: .5rem;
          transition: transform .12s ease, box-shadow .2s ease, background-color .2s ease;
          -webkit-tap-highlight-color: transparent;
        }
        .cm-error-btn:active { transform: scale(.985); }
        .cm-error-btn:focus-visible {
          outline: 2px solid #D32F2F;
          outline-offset: 2px;
        }
        .cm-error-btn--primary {
          border: none;
          background: #D32F2F;
          color: #FFFFFF;
          box-shadow: 0 6px 16px rgba(211, 47, 47, 0.24);
        }
        .cm-error-btn--primary:hover { background: #C62828; }
        .cm-error-btn--primary:disabled { opacity: .75; cursor: default; transform: none; }
        .cm-error-btn--ghost {
          border: 1px solid #E5E0D6;
          background: #FFFFFF;
          color: #1E1E1E;
        }
        .cm-error-btn--ghost:hover { background: #FBF7F0; }
        .cm-error-spin {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.45);
          border-top-color: #FFFFFF;
          border-radius: 50%;
          animation: cm-error-spin .8s linear infinite;
        }
        @keyframes cm-error-spin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) {
          .cm-error-card { animation: none; }
          .cm-error-cover img { transition: none; }
          .cm-error-spin { animation-duration: 2s; }
        }
        .cm-error-extra { margin-top: 1rem; }
        .cm-error-footer {
          margin-top: 1.15rem;
          padding-top: 1rem;
          border-top: 1px solid #F0EBE2;
        }
        .cm-error-help {
          margin: 0;
          color: #6B6B6B;
          font-size: .8rem;
          line-height: 1.5;
        }
        .cm-error-help a { color: #D32F2F; font-weight: 600; text-decoration: none; word-break: break-word; }
        .cm-error-code {
          display: inline-block;
          margin: .55rem 0 0;
          padding: .2rem .55rem;
          border-radius: 999px;
          background: #F6F3EE;
          color: #8A8478;
          font-size: .7rem;
          letter-spacing: .02em;
          font-variant-numeric: tabular-nums;
        }
        @media (min-width: 640px) {
          .cm-error-cover { max-height: none; }
          .cm-error-body { padding: .5rem 2rem 1.6rem; }
        }
      `}</style>
      <div className="cm-error-card">
        <figure className="cm-error-cover" data-state={cover} aria-hidden="true">
          <div className="cm-error-cover-lqip" style={{ backgroundImage: `url(${ERROR_COVER_LQIP})` }} />
          <picture>
            <source type="image/webp" srcSet={COVER_WEBP} />
            <img
              src={COVER_JPG}
              alt=""
              width={800}
              height={534}
              decoding="async"
              onLoad={() => setCover('ok')}
              onError={() => setCover('fail')}
            />
          </picture>
          <span className="cm-error-brand">
            <span className="cm-error-brand-a">consulta</span>
            <span className="cm-error-brand-b">mais</span>
          </span>
        </figure>
        <div className="cm-error-body">
          <h1 className="cm-error-title">{v.title}</h1>
          <div className="cm-error-divider" aria-hidden="true">◆</div>
          <p className="cm-error-text">{v.text}</p>
          <p className="cm-error-reassure">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 3l7 3v5c0 4.4-3 8.3-7 9.5C8 19.3 5 15.4 5 11V6l7-3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{v.reassurance}</span>
          </p>
          <div className="cm-error-actions">
            {onPrimary && (
              <button type="button" className="cm-error-btn cm-error-btn--primary" onClick={onPrimary} disabled={busy}>
                {busy && <span className="cm-error-spin" aria-hidden="true" />}
                {primaryLabel || v.primary}
              </button>
            )}
            {onHelp && (
              <button type="button" className="cm-error-btn cm-error-btn--ghost" onClick={onHelp}>
                Falar com o suporte
              </button>
            )}
            {onHome && (
              <button type="button" className="cm-error-btn cm-error-btn--ghost" onClick={onHome}>
                Voltar ao início
              </button>
            )}
          </div>
          {children && <div className="cm-error-extra">{children}</div>}
          <div className="cm-error-footer">
            <p className="cm-error-help">
              Se continuar, escreva para{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              {errorId ? ' e informe o código abaixo.' : '.'}
            </p>
            {errorId && <p className="cm-error-code">Código: {errorId}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
